import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "@/i18n/LangRouting";
import { Search, Headphones, FileText } from "lucide-react";
import { useExhibition } from "@/visitor/ExhibitionShell";
import Header from "@/visitor/Header";
import MiniPlayer from "@/visitor/MiniPlayer";
import NudgeBanner from "@/visitor/NudgeBanner";
import QRScanButton from "@/visitor/QRScanner";
import { usePlayer } from "@/visitor/PlayerContext";
import { fileUrl } from "@/lib/api";

function ArtworkCard({ a, slug, lang, t }) {
  const c = a.content?.[lang];
  return (
    <Link to={`/e/${slug}/a/${a.slug}`} data-testid={`artwork-card-${a.id}`} className="card-row flex gap-4 items-center border border-[var(--line)] p-3 fade-up">
      <div className="w-20 h-20 flex-none overflow-hidden">
        {a.image_url ? <img src={fileUrl(a.image_url)} alt={a.title} className="w-full h-full object-cover" /> : <div className="art-placeholder w-full h-full">img</div>}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-display text-xl uppercase leading-tight truncate">{a.title}</h3>
        <p className="text-xs text-[var(--muted)] mt-1">{a.place}{a.place && a.year ? " · " : ""}{a.year}</p>
      </div>
      <span className="text-[var(--muted)]" title={c?.audio_url ? "Audio" : t("text_only")}>{c?.audio_url ? <Headphones size={18} /> : <FileText size={18} />}</span>
    </Link>
  );
}
import { giftName, discountOf } from "@/lib/gifts";

export default function ArtworkListPage() {
  const { slug, exhibition, lang, visitor, t } = useExhibition();
  const { track } = usePlayer();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const welcome = params.get("welcome");
  const list = useMemo(() => exhibition.artworks.filter((a) => a.title.toLowerCase().includes(q.trim().toLowerCase())), [exhibition, q]);

  return (
    <div className={`min-h-screen ${track ? "pb-44" : "pb-10"}`} data-testid="artwork-list-page">
      <Header exhibition={exhibition} slug={slug} />
      <NudgeBanner />
      <div className="px-5 lg:px-12 max-w-6xl mx-auto">
        {welcome && visitor && (
          <div className="mt-5 border border-[var(--accent)] bg-[var(--accent)]/10 px-4 py-3 text-sm flex justify-between items-center gap-3 fade-up" data-testid="signup-confirmation-banner">
            <span>{visitor.gift === "none" ? t("welcome_newsletter") : visitor.gift === "poster" ? <>{t("welcome_poster", { gift: giftName(exhibition, lang, t) })} <Link to={`/e/${slug}/voucher`} className="underline text-accent" data-testid="banner-voucher-link">{t("view_voucher")}</Link></> : t("welcome_discount", { discount: discountOf(exhibition) })}</span>
            <button type="button" onClick={() => setParams({}, { replace: true })} className="text-xs uppercase tracking-widest text-[var(--muted)] hover:text-white" data-testid="banner-dismiss">{t("close")}</button>
          </div>
        )}
        <h1 className="font-display text-4xl sm:text-5xl uppercase mt-8" data-testid="artwork-list-title">{t("all_artworks")}</h1>
        <div className="flex gap-3 mt-6">
          <label className="field flex items-center gap-3 flex-1 cursor-text">
            <Search size={18} className="text-[var(--muted)] flex-none" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")} className="bg-transparent outline-none flex-1 min-w-0" data-testid="artwork-search-input" />
          </label>
          <QRScanButton label={t("scan_qr")} />
        </div>
        <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="artwork-list">
          {list.map((a) => <ArtworkCard key={a.id} a={a} slug={slug} lang={lang} t={t} />)}
          {list.length === 0 && <p className="text-[var(--muted)] text-sm col-span-full" data-testid="artwork-list-empty">{t("no_results", { query: q })}</p>}
        </div>
      </div>
      <MiniPlayer />
    </div>
  );
}
